import {defineStore} from 'pinia';
import {useAuthentificationStore} from './authentification';
import type {
  Immobilisation,
  ReleveKilometrique,
  Vehicule,
  VehiculeLight,
  ServiceCopilot,
  Alerte,
  DeposerVehiculeCommand,
} from '~/types/typesVehicule';
import { useRendezVousStore } from '~/stores/rendez-vous';

export const useVehiculeStore = defineStore('vehicule', () => {
  // 1. STATE & PERSISTANCE
  const vehicule = ref<Vehicule | null>(JSON.parse(localStorage.getItem('vehicule') ?? 'null'));
  const vehiculeLight = ref<VehiculeLight | null>(JSON.parse(localStorage.getItem('vehiculeLight') ?? 'null'));
  const savedImmatriculation = ref<string>(localStorage.getItem('savedImmatriculation') ?? '');

  // Persistance des données annexes
  const relevesKilometriques = ref<ReleveKilometrique[]>(
    JSON.parse(localStorage.getItem('relevesKilometriques') ?? '[]'),
  );
  const immobilisation = ref<Immobilisation | null>(JSON.parse(localStorage.getItem('immobilisation') ?? 'null'));

  // --- DEPENDENCIES ---
  const authentificationStore = useAuthentificationStore();
  const fournisseursStore = useFournisseursStore();
  const toasterStore = useToasterStore();
  const loaderStore = useLoaderStore();
  const notificationsStore = useNotificationStore();

  // 2. WATCHERS
  watch(
    vehicule,
    (newValue) => {
      if (newValue) localStorage.setItem('vehicule', JSON.stringify(newValue));
      else localStorage.removeItem('vehicule');
    },
    {deep: true},
  );

  watch(vehiculeLight, (newValue) => {
    if (newValue) localStorage.setItem('vehiculeLight', JSON.stringify(newValue));
    else localStorage.removeItem('vehiculeLight');
  });

  watch(
    relevesKilometriques,
    (newValue) => {
      localStorage.setItem('relevesKilometriques', JSON.stringify(newValue));
    },
    {deep: true},
  );

  watch(
    immobilisation,
    (newValue) => {
      if (newValue) localStorage.setItem('immobilisation', JSON.stringify(newValue));
      else localStorage.removeItem('immobilisation');
    },
    {deep: true},
  );

  // 3. RESET
  function $reset() {
    vehicule.value = null;
    vehiculeLight.value = null;
    relevesKilometriques.value = [];
    immobilisation.value = null;
    localStorage.removeItem('vehicule');
    localStorage.removeItem('vehiculeLight');
    localStorage.removeItem('relevesKilometriques');
    localStorage.removeItem('immobilisation');
  }

  // 4. GETTERS
  // Ici on utilise isAuthenticated() car c'est une computed (réactif), donc ça marche même avec la dépendance circulaire
  const isModeConnecte = computed(() => authentificationStore.isAuthenticated() && !!vehicule.value);

  const getVehiculeCible = computed(() => {
    if (isModeConnecte.value) return vehicule.value;
    return vehiculeLight.value;
  });

  const getIdActif = () => {
    const target = getVehiculeCible.value;
    if (!target) return '';
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return 'id' in target ? target.id : (target as any).idActif || '';
  };

  const getImmatriculation = () => getVehiculeCible.value?.immatriculation ?? '';
  const getNumeroDossier = () => getVehiculeCible.value?.numeroDossier ?? '';

  //ordre static pour avoir l'ordre souhaité
  const ordrePrestations = [
    'REVISION',
    'REPARATION',
    'PNEUMATIQUE',
    'CONTROLE_TECHNIQUE',
    'CARROSSERIE',
    'VITRAGE',
  ];

  const prestationsTriees = computed(() => {
    const prestations = [...(getPrestations() as PrestationsVehiculeCopilotEntry[])];
    return prestations.sort(
      (a, b) =>
        ordrePrestations.indexOf(a.prestationCopilot) -
        ordrePrestations.indexOf(b.prestationCopilot)
    );
  });

  const getPrestations = (index?: number) => {
    const cible = getVehiculeCible.value;
    const prestations = [...(cible?.prestationsCopilot ?? [])].sort(
      (a, b) =>
        ordrePrestations.indexOf(a.prestationCopilot) -
        ordrePrestations.indexOf(b.prestationCopilot)
    );
    if (index !== undefined) return prestations[index];
    return prestations;
  };

  const getIdEntite = () => (getVehiculeCible.value as Vehicule)?.entiteClientRepresentation?.id ?? '';
  const getIdClient = () => (getVehiculeCible.value as Vehicule)?.idClient ?? '';
  const getServices = () => (getVehiculeCible.value as Vehicule)?.services ?? [];
  const getCommentaire = (prestation: string) => (getVehiculeCible.value as Vehicule)?.commentaires?.[prestation];
  const getDernierReleveKilometrique = () => (getVehiculeCible.value as Vehicule)?.dernierReleveKilometrique ?? 0;

  const getVehicule = (): Vehicule | null => vehicule.value;
  const getVehiculeLight = (): VehiculeLight | null => vehiculeLight.value;
  const getRelevesKilometriques = () => relevesKilometriques.value;
  const getImmobilisation = () => immobilisation.value;
  const getSavedImmatriculation = () => savedImmatriculation.value;

  const getProchainesVisites = () => {
    if (!isModeConnecte.value || !vehicule.value) return [];
    return getVisites(vehicule.value.alertes ?? [], vehicule.value);
  };

  const isServiceAutorise = (service: ServiceCopilot): boolean => {
    return (getVehiculeCible.value as Vehicule)?.services?.includes(service) ?? false;
  };

  const prochaineVisite = () => {
    if (!isModeConnecte.value || !vehicule.value) return null;
    const visites = getVisites(vehicule.value.alertes ?? [], vehicule.value);
    if (visites.length) {
      const errorVisite = visites.find((v) => v.alerte === 'error');
      return errorVisite || visites[0];
    }
    return null;
  };

  // 5. HELPERS
  const fetchLogoEntite = async (entiteClientId: string, idActif: string) => {
    const {data} = await useMyFetch<Blob>('/copilot/entitees/logo', {
      method: 'GET',
      headers: {'Content-Type': 'application/json', Authorization: 'Bearer ' + authentificationStore.getToken()},
      params: {idEntite: entiteClientId, idVehicule: idActif},
      responseType: 'blob',
    });
    if (data.value && data.value.size > 0) return URL.createObjectURL(new Blob([data.value]));
    return null;
  };

  // 6. ACTIONS
  const fetchVehicule = async (id: string) => {
    loaderStore.showLoader();

    vehiculeLight.value = null;
    localStorage.removeItem('vehiculeLight');

    const paramIdVehicule = id ? `?paramIdVehicule=${id}` : '';
    const {data, error} = await useMyFetch<Vehicule>('/copilot/vehicules' + paramIdVehicule, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + authentificationStore.getToken(),
      },
    });

    if (data.value) {
      const services: ServiceCopilot[] = data.value.services.map((service) => ServiceCopilot[service]);
      const logoEntite = await fetchLogoEntite(data.value.entiteClientRepresentation.id, data.value.id);

      vehicule.value = {...data.value, services, logoEntite};

      await Promise.all([
        fetchAlertes(),
        fournisseursStore.fetchFournisseurFavori(),
        notificationsStore.recupererLesNotifications(),
        recupererInfosImmobilisation(),
        useRendezVousStore().recupererDemandesRendezVous(),
      ]);
    } else if (error.value) {
      toasterStore.afficherLeToaster('error', 'messageErreur.erreurRecupInfosVehicule');
    }
    loaderStore.hideLoader();
  };

  const fetchVehiculeLight = async (payload: {
    immatriculation: string;
    rememberMe?: boolean;
    recaptchaToken: string;
  }) => {
    loaderStore.showLoader();

    const {recaptchaToken, ...values} = payload;

    vehicule.value = null;
    localStorage.removeItem('vehicule');

    const immat = encodeURIComponent(values.immatriculation);

    const rememberMeParam = values.rememberMe ? '?rememberMe=true' : '';
    const {data, error} = await useMyFetch<VehiculeLight>(`/copilot/non-connecte/vehicule/${immat}${rememberMeParam}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Recaptcha-Token': recaptchaToken,
      },
    });

    if (data.value) {
      if (values.rememberMe) {
        localStorage.setItem('savedImmatriculation', values.immatriculation);
        savedImmatriculation.value = values.immatriculation;
      } else {
        localStorage.removeItem('savedImmatriculation');
        savedImmatriculation.value = '';
      }

      authentificationStore.setModeConnexion('guest');
      vehiculeLight.value = data.value;
      navigateTo('/recherche-fournisseurs/recherche');
    } else if (error.value) {
      if (values.immatriculation && values.immatriculation.includes('@')) {
        toasterStore.afficherLeToaster('error', 'messageErreur.immatAttendue');
      } else {
        toasterStore.afficherLeToaster('error', 'messageErreur.immatNonTrouve');
      }
    }
    loaderStore.hideLoader();
  };

  const fetchAlertes = async () => {
    if (!vehicule.value) return;
    const {data} = await useMyFetch<Alerte[]>('/copilot/vehicules/' + getIdActif() + '/alertes', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + authentificationStore.getToken(),
      },
    });
    if (data.value) vehicule.value.alertes = data.value;
  };

  const fetchDerniersRelevesKilometriques = async () => {
    if (!isModeConnecte.value) return;
    const {data, error} = await useMyFetch<ReleveKilometrique[]>(
      '/copilot/vehicules/' + getIdActif() + '/recupererDerniersReleves',
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + authentificationStore.getToken(),
        },
      },
    );
    if (data.value) relevesKilometriques.value = data.value;
    else if (error.value) toasterStore.afficherLeToaster('error', 'messageErreur.erreurRecupInfosVehicule');
  };

  const ajouterReleveKilometrique = async (releveKilometrique: number) => {
    if (!isModeConnecte.value) return;
    loaderStore.showLoader();
    const {error} = await useMyFetch<string>('/copilot/vehicules/' + getIdActif() + '/ajouterReleveKilometrique', {
      method: 'POST',
      body: {kilometrage: releveKilometrique, source: 'Copilot', date: new Date(Date.now())},
      headers: {'Content-Type': 'application/json', Authorization: 'Bearer ' + authentificationStore.getToken()},
    });
    if (!error.value) {
      await fetchVehicule(getIdActif());
      toasterStore.afficherLeToaster('success', 'messageSucces.majReleveKilometrique');
      useLogging({action: 'AJOUTER_RELEVE_KILOMETRIQUE'}, 'logEventButtonClick');
    } else {
      toasterStore.afficherLeToaster('error', 'messageErreur.erreurAjoutReleveKilometrique');
    }
    loaderStore.hideLoader();
  };

  const ajouterEtatDesLieux = async (values: object) => {
    if (!isModeConnecte.value) return;
    loaderStore.showLoader();
    const {error} = await useMyFetch<string>('/copilot/vehicules/' + getIdActif() + '/ajouterEtatDesLieux', {
      method: 'POST',
      body: values,
      headers: {Authorization: 'Bearer ' + authentificationStore.getToken()},
    });
    if (error.value) {
      toasterStore.afficherLeToaster('error', 'messageErreur.erreurAjoutEtatDesLieux');
      useLogging({action: 'DEMANDER_TRANSMISSION_ETAT_DES_LIEUX'}, 'logEventButtonClick');
    } else {
      toasterStore.afficherLeToaster('success', 'messageSucces.etatDesLieuxEnvoye');
    }
    loaderStore.hideLoader();
  };

  const deposerVehicule = async (values: DeposerVehiculeCommand) => {
    if (!isModeConnecte.value) return;
    loaderStore.showLoader();
    const {error} = await useMyFetch<string>('/copilot/immobilisations/' + getIdActif(), {
      method: 'POST',
      body: values,
      headers: {Authorization: 'Bearer ' + authentificationStore.getToken()},
    });
    await recupererInfosImmobilisation();
    if (error.value) toasterStore.afficherLeToaster('error', 'messageErreur.erreurDepotVehicule');
    else toasterStore.afficherLeToaster('success', 'messageSucces.depotVehicule');
    loaderStore.hideLoader();
  };

  const recupererVehiculeImmobilise = async (dateRecuperation: Date) => {
    if (!isModeConnecte.value) return;
    loaderStore.showLoader();
    const params: RecupererVehiculeCommand = {
      dateDepot: immobilisation.value?.dateDepot,
      dateRecuperation: dateRecuperation,
      prestations: immobilisation.value?.prestations,
      idFournisseur: immobilisation.value?.idFournisseur,
      nomGarage: immobilisation.value?.nomFournisseur,
      adresseGarage: immobilisation.value?.adresseFournisseur,
    };
    const {error} = await useMyFetch<Vehicule>('/copilot/immobilisations/' + getIdActif(), {
      method: 'PUT',
      body: params,
      headers: {Authorization: 'Bearer ' + authentificationStore.getToken()},
    });
    await recupererInfosImmobilisation();
    if (error.value) toasterStore.afficherLeToaster('error', 'messageErreur.erreurRecupVehicule');
    else toasterStore.afficherLeToaster('success', 'messageSucces.recupVehicule');
    loaderStore.hideLoader();
  };

  const recupererInfosImmobilisation = async () => {
    if (!isModeConnecte.value) return;
    const {data} = await useMyFetch<Immobilisation>('/copilot/immobilisations/' + getIdActif(), {
      method: 'GET',
      headers: {Authorization: 'Bearer ' + authentificationStore.getToken()},
    });
    immobilisation.value = data.value;
  };

  const abonnementNotifications = async (token: string) => {
    if (!isModeConnecte.value) return;
    await useMyFetch<string>('/copilot/notifications/' + getIdActif() + '/souscriptionNotifications', {
      method: 'POST',
      body: {idActif: getIdActif(), token, updatedAt: new Date(Date.now())},
      headers: {'Content-Type': 'application/json', Authorization: 'Bearer ' + authentificationStore.getToken()},
    });
  };

  const desabonnementNotifications = async (idActif: string, token: string) => {
    if (!isModeConnecte.value) return;
    await useMyFetch<string>('/copilot/notifications/' + idActif + '/desouscriptionNotifications/' + token, {
      method: 'POST',
      headers: {'Content-Type': 'application/json', Authorization: 'Bearer ' + authentificationStore.getToken()},
    });
  };

  return {
    $reset,
    vehicule,
    vehiculeLight,
    isModeConnecte,
    getIdActif,
    getImmatriculation,
    getPrestations,
    prestationsTriees,
    getNumeroDossier,
    getVehicule,
    getVehiculeLight,
    getIdEntite,
    getImmobilisation,
    getRelevesKilometriques,
    getProchainesVisites,
    getCommentaire,
    getServices,
    getIdClient,
    getDernierReleveKilometrique,
    getSavedImmatriculation,
    isServiceAutorise,
    prochaineVisite,
    fetchVehicule,
    fetchVehiculeLight,
    fetchDerniersRelevesKilometriques,
    ajouterReleveKilometrique,
    ajouterEtatDesLieux,
    abonnementNotifications,
    desabonnementNotifications,
    deposerVehicule,
    recupererInfosImmobilisation,
    recupererVehiculeImmobilise,
    fetchLogoEntite,
  };
});
